import DOMPurify from 'dompurify';

/** Tags allowed from the Create a File Quill toolbar. */
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'a', 'div', 'span'];

const ALLOWED_ATTR = {
    a: ['href', 'title', 'rel', 'target'],
    '*': ['class'],
};

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'ftp:']);

const decodeHtmlEntities = html => {
    try {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = html;
        return textarea.value;
    } catch {
        return html;
    }
};

const sanitizeLinkHref = href => {
    if (!href || typeof href !== 'string') return '';

    const trimmed = href.trim();
    if (!trimmed) return '';

    const lower = trimmed.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:')) {
        return '';
    }

    try {
        const parsed = new URL(trimmed, window.location.origin);
        if (!SAFE_LINK_PROTOCOLS.has(parsed.protocol)) return '';
        return parsed.href;
    } catch {
        if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
            return trimmed;
        }
        return '';
    }
};

const scrubLinks = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('a').forEach(anchor => {
        const safeHref = sanitizeLinkHref(anchor.getAttribute('href'));
        if (!safeHref) {
            anchor.removeAttribute('href');
            anchor.removeAttribute('target');
            anchor.removeAttribute('rel');
            return;
        }
        anchor.setAttribute('href', safeHref);
        anchor.setAttribute('rel', 'noopener noreferrer');
        anchor.setAttribute('target', '_blank');
    });

    return doc.body.innerHTML.trim();
};

/**
 * Strip scripts, event handlers, and dangerous markup before exporting rich text to .txt/.docx.
 */
export const sanitizeRichTextForFileExport = html => {
    if (!html?.trim()) return '';

    const decoded = decodeHtmlEntities(html);

    const sanitized = DOMPurify.sanitize(decoded, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
        KEEP_CONTENT: true,
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'meta', 'link', 'base'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'style'],
    });

    return scrubLinks(sanitized);
};
